import { FC } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PopulationSegment } from "@models/populationSegment.model";

export interface SegmentFormValues {
  name: string;
}

interface Props {
  show: boolean;
  segment: PopulationSegment | null; // null = create
  /** Lowercased names of the org's other segments, for instant duplicate feedback. */
  existingNames: string[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (values: SegmentFormValues) => Promise<void>;
}

const SegmentFormModal: FC<Props> = ({ show, segment, existingNames, isSaving, onClose, onSave }) => {
  const isEdit = segment != null;

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required("Name is required")
      .max(255, "Name must be 255 characters or fewer")
      .test(
        "unique-per-org",
        "A segment with this name already exists for this organization",
        (value) => !value || !existingNames.includes(value.trim().toLowerCase()),
      ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: segment?.name ?? "",
    } as SegmentFormValues,
    validationSchema,
    onSubmit: async (values: SegmentFormValues) => {
      try {
        await onSave({ name: values.name.trim() });
      } catch {
        // Server-side errors (e.g. duplicate 409) are surfaced via toast by the hook;
        // keep the modal open so the user can correct and retry.
      }
    },
  });

  return (
    <Modal show={show} onHide={onClose} centered backdrop={isSaving ? "static" : true}>
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Header closeButton={!isSaving}>
          <Modal.Title as="h5">{isEdit ? "Edit segment" : "New segment"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-1" controlId="segment-name">
            <Form.Label className="mb-1 fw-normal">Name</Form.Label>
            <Form.Control
              type="text"
              autoFocus
              {...formik.getFieldProps("name")}
              isInvalid={!!(formik.touched.name && formik.errors.name)}
              disabled={isSaving}
              placeholder="e.g. summer_fall_all2026"
            />
            <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSaving}>
            {isSaving ? "Saving…" : isEdit ? "Save changes" : "Create segment"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SegmentFormModal;
